import { IScope, IOnInit, IPromise } from "angular";

import {
  Chart,
  ChartConfiguration,
  ChartData,
  ChartDataset,
  registerables,
  ScaleOptionsByType,
} from "chart.js";
import { IQuery } from "../../../../records/server/query";
import {
  vsName as serverUtilityName,
  ServerUtility,
} from "../../../../../app/records/server/serverUtility";
import { ProgressivesContext } from "../../model/progressivesContext";
import { ChartOptions, Scale } from "chart.js";
import { max, maxBy, orderBy } from "lodash";
import { ChartJsDirectiveController } from "../../../../ui/chart/controller";

Chart.register(...registerables);

interface ProgressiveDailyMachine {
  ProgressiveDailyPeriodId: number;
  MachineId: number;
  MachineConfigurationId: number;
  ManufacturerName: string;
  SerialNumber: string;
  SlotNumber: string;
  Section: string;
  Bank: string;
  Slot: string;
  Location: string;
  Complete: boolean;
  RequiredReadings: number;
  Readings: number;
}

interface SectionData {
  section: string;
  completeCount: number;
  incompleteCount: number;
  totalCount: number;
  completePercent?: number;
  incompletePercent?: number;
}

interface AggregateData {
  totalAggregate: SectionData;
  sections: SectionData[];
}

interface ChartType {
  displayText: string;
  getMaxY: (sections: SectionData[]) => number;
  ticks: (value: string | number) => string;
  title: string;
  tooltips: (section: SectionData) => string[];
  chartData: (sections: SectionData[]) => ChartData;
}

export class DailyReadingsGraphController implements IOnInit {
  static $inject = ["$scope", serverUtilityName];
  private data: AggregateData = {
    totalAggregate: this.createSectionData(),
    sections: [],
  };
  private progressivesContext!: ProgressivesContext;
  private chartData: ChartData | undefined;

  private readonly chartTypes: ChartType[] = [
    {
      displayText: "Percentage (%)",
      getMaxY: () => 100,
      ticks: (value) => `${value}%`,
      title: "Progressive Completion % by Section",
      tooltips: (sectionData) => [
        `Complete Count: ${sectionData.completeCount}`,
        `Incomplete Count: ${sectionData.incompleteCount}`,
        `Total Count: ${sectionData.totalCount}`,
      ],
      chartData: (sections) => {
        return {
          labels: sections.map((s) => s.section),
          datasets: [
            {
              label: "Complete %",
              backgroundColor: "#43b243",
              data: sections.map((s) => s.completePercent ?? 0),
            },
            {
              label: "Incomplete %",
              hidden: true,
              backgroundColor: "blue",
              data: sections.map((s) => s.incompletePercent ?? 100),
            },
          ],
        };
      },
    },
    {
      displayText: "Count",
      getMaxY: (sections: SectionData[]) =>
        maxBy(sections, (s) => s.totalCount)?.totalCount ?? 0,
      ticks: (value) => value.toString(),
      title: "Progressive Machine Count by Section",
      tooltips: (sectionData) => {
        const completePercent =
          Math.round(sectionData.completePercent! * 1000) / 1000;
        return [
          `Complete Percentage: ${completePercent}%`,
          `Incomplete Percentage: ${100 - completePercent}%`,
          `Total Count: ${sectionData.totalCount}`,
        ];
      },
      chartData: (sections) => {
        return {
          labels: sections.map((s) => s.section),
          datasets: [
            {
              label: "Complete Count",
              backgroundColor: "#43b243",
              data: sections.map((s) => s.completeCount),
            },
            {
              label: "Incomplete Count",
              backgroundColor: "blue",
              data: sections.map((s) => s.incompleteCount),
            },
          ],
        };
      },
    },
  ];
  private chartType: ChartType = this.chartTypes[0];

  private chartOptions: ChartOptions = {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          afterBody: (tooltipItems) => {
            const { label } = tooltipItems[0];
            const sectionData = this.data.sections.find(
              (s) => s.section === label
            );

            return sectionData ? this.chartType.tooltips(sectionData) : [];
          },
        },
      },
      title: {
        display: true,
        text: "Completion Percentage by Section",
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Sections",
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: 100,
        min: 0,
        title: {
          display: true,
        },
        ticks: {
          callback: (value) => `${value}%`,
        },
      },
    },
  };

  constructor(
    private readonly $scope: IScope,
    private serverUtility: ServerUtility
  ) {}

  $onInit() {
    this.$scope.$watchGroup(
      [() => this.progressivesContext.date, () => this.chartType],
      (newValues, oldValues) => {
        const newDate = <Date>newValues[0];
        const oldDate = <Date>oldValues[0];

        newDate.getTime() === oldDate.getTime() && this.chartData //if data is initialized and date hasn't changed, just update chart
          ? this.chartChanged()
          : this.fetchAndUpdateChartData();
      }
    );
  }

  chartChanged() {
    if (!this.chartType) return;

    const yScale = this.chartOptions.scales?.y ?? {};

    (yScale.ticks ?? {}).callback = this.chartType.ticks;
    yScale.max = this.chartType.getMaxY(this.data.sections);
    (<any>yScale).title.text = this.chartType.displayText;
    (this.chartOptions.plugins?.title ?? {}).text = this.chartType.title;
    this.chartData = this.chartType.chartData(this.data.sections);
  }

  private fetchAndUpdateChartData() {
    return this.fetchChartData().then((aggregateData) => {
      this.data = aggregateData;
      this.chartChanged();
    });
  }

  /**
   * Fetches data from the API using HTTP GET
   */
  private fetchChartData(): IPromise<AggregateData> {
    return this.serverUtility
      .query<any[]>({
        url: "/api/machines/progressives/ProgressiveDailyMachine/bulk",
        query: {
          ProgressiveDailyPeriodDate: this.progressivesContext.date,
        },
      })
      .then((queryResults) => {
        const results = <ProgressiveDailyMachine[]>(<unknown>queryResults);
        return this.getAggregateData(results);
      })
      .catch((error) => {
        console.error("Error fetching data from API:", error);
        return {
          totalAggregate: this.createSectionData(),
          sections: <SectionData[]>[],
        };
      });
  }

  private getAggregateData(machines: ProgressiveDailyMachine[]): AggregateData {
    const totalAggregate: SectionData = this.createSectionData();
    const data = machines.reduce<{
      [section: string]: SectionData;
    }>((machine, datum) => {
      const sectionData =
        machine[datum.Section] ??
        (machine[datum.Section] = this.createSectionData(datum.Section));

      totalAggregate.totalCount += 1;
      sectionData.totalCount += 1;

      if (datum.Complete) {
        totalAggregate.completeCount += 1;
        sectionData.completeCount += 1;
      } else {
        totalAggregate.incompleteCount += 1;
        sectionData.incompleteCount += 1;
      }

      return machine;
    }, {});

    const sections = Object.values(data);
    sections.forEach(DailyReadingsGraphController.setCompletePercentage);
    DailyReadingsGraphController.setCompletePercentage(totalAggregate);

    return {
      totalAggregate,
      sections: orderBy(sections, (s) => s.section),
    };
  }

  private createSectionData(section?: string): SectionData {
    return {
      section: section ?? "",
      completeCount: 0,
      incompleteCount: 0,
      totalCount: 0,
      completePercent: 0,
      incompletePercent: 100,
    };
  }

  private static setCompletePercentage(sectionData: SectionData) {
    const completePercent = (sectionData.completePercent =
      (100 * sectionData.completeCount) / sectionData.totalCount);
    sectionData.incompletePercent = 100 - completePercent;
  }
}
